import React from "react";

interface AIDialogMessage {
  agent: string;
  role: string;
  content: string;
}

interface AIDialogRound {
  round_index: number;
  messages: AIDialogMessage[];
}

interface DebateRound {
  round_index: number;
  agent_responses: Record<string, string>;
  critiques: Record<string, string>;
}

interface DebateJudgeResult {
  winning_agent: string;
  rationale: string;
}

interface DebateOrchestratorOutput {
  rounds: DebateRound[];
  judge: DebateJudgeResult;
  final_answer: any;
}

interface AIDialogLog {
  rounds?: AIDialogRound[];
  debate?: DebateOrchestratorOutput;
}

interface LLMDialogLogProps {
  dialogLog?: AIDialogLog | null;
}

const LLMDialogLog: React.FC<LLMDialogLogProps> = ({ dialogLog }) => {
  if (!dialogLog) {
    return null;
  }

  const hasRounds = dialogLog.rounds && dialogLog.rounds.length > 0;
  const hasDebate = dialogLog.debate && dialogLog.debate.rounds && dialogLog.debate.rounds.length > 0;

  if (!hasRounds && !hasDebate) {
    return null;
  }

  const formatContent = (content: string): string => {
    // Preserve line breaks and basic formatting
    return content;
  };

  return (
    <div className="card full-width">
      <section className="analysis-section">
        <h3>LLM Dialog Log</h3>
        <div className="section-body">
          <div className="llm-dialog-container">
            {/* Classic Dialog Rounds */}
            {hasRounds && (
              <div className="dialog-section">
                <h4 className="dialog-section-title">Agent Dialog</h4>
                {dialogLog.rounds!.map((round, idx) => (
                  <div key={idx} className="dialog-round">
                    <div className="dialog-round-header">
                      Round {round.round_index}
                    </div>
                    {round.messages.map((message, msgIdx) => (
                      <div key={msgIdx} className="dialog-message">
                        <div className="dialog-message-header">
                          <span className="dialog-agent">{message.agent}</span>
                          <span className="dialog-role">({message.role})</span>
                        </div>
                        <div className="dialog-message-content">
                          {formatContent(message.content)}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {/* Debate Rounds */}
            {hasDebate && (
              <div className="dialog-section">
                <h4 className="dialog-section-title">Debate Dialog</h4>
                {dialogLog.debate!.rounds.map((round, idx) => (
                  <div key={idx} className="dialog-round">
                    <div className="dialog-round-header">
                      Debate Round {round.round_index}
                    </div>
                    
                    {/* Agent Responses */}
                    {Object.entries(round.agent_responses || {}).map(([agent, response], respIdx) => (
                      <div key={`resp-${respIdx}`} className="dialog-message">
                        <div className="dialog-message-header">
                          <span className="dialog-agent">{agent}</span>
                          <span className="dialog-role">(Response)</span>
                        </div>
                        <div className="dialog-message-content">
                          {formatContent(response)}
                        </div>
                      </div>
                    ))}

                    {/* Critiques */}
                    {Object.entries(round.critiques || {}).map(([agent, critique], critIdx) => (
                      <div key={`crit-${critIdx}`} className="dialog-message dialog-message-critique">
                        <div className="dialog-message-header">
                          <span className="dialog-agent">{agent}</span>
                          <span className="dialog-role">(Critique)</span>
                        </div>
                        <div className="dialog-message-content">
                          {formatContent(critique)}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}

                {/* Judge Result */}
                {dialogLog.debate!.judge && (
                  <div className="dialog-round">
                    <div className="dialog-round-header">
                      Judge Decision
                    </div>
                    <div className="dialog-message">
                      <div className="dialog-message-header">
                        <span className="dialog-agent">Judge</span>
                        <span className="dialog-role">(Decision)</span>
                      </div>
                      <div className="dialog-message-content">
                        <strong>Winning Agent:</strong> {dialogLog.debate!.judge.winning_agent}
                        <br />
                        <strong>Rationale:</strong> {formatContent(dialogLog.debate!.judge.rationale)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default LLMDialogLog;



